import React, { useEffect, useRef } from "react";

type Variables = Record<string, any>;

type EventLogCtxValue = {
  variables: Variables;
  refetch: (variables?: Variables) => Promise<any>;
};

const EventLogCtx = React.createContext({} as EventLogCtxValue);

export const EventLogDataProvider = ({
  children,
  value
}: {
  children: React.ReactNode;
  value: EventLogCtxValue;
}) => <EventLogCtx.Provider value={value}>{children}</EventLogCtx.Provider>;

export const useEventLogData = (
  refetchFn?: () => Promise<any>,
  variables?: Variables
) => {
  const ctx = React.useContext(EventLogCtx);
  const refetchTimers = useRef(new Map());
  const refetch = refetchFn || ctx.refetch || (() => Promise.resolve());

  function _refetch() {
    const _variables = ctx.variables || variables;

    const limit = _variables.skip + _variables.limit;

    const refetchVariables = _variables
      ? { ..._variables, limit, skip: 0 }
      : {};

    return refetch(refetchVariables);
  }

  useEffect(() => {
    return () => {
      refetchTimers.current.forEach((timeout) => clearTimeout(timeout));
    };
  }, []);

  async function refetchAndCheck(id: string, tries = 5) {
    const { data } = await _refetch();

    if (!data.consultantEvents.some((d) => d._id === id) && tries > 0) {
      const timeout = setTimeout(() => refetchAndCheck(id, tries - 1), 1000);
      refetchTimers.current.set(id, timeout);
    } else {
      refetchTimers.current.delete(id); // Clear the entry if refetch is done
    }
  }

  function refetchWithPolling(eventId?: string) {
    if (refetchTimers.current.has(eventId)) {
      clearTimeout(refetchTimers.current.get(eventId));
      refetchTimers.current.delete(eventId);
    }

    const timeout = setTimeout(() => refetchAndCheck(eventId, 5), 500);
    refetchTimers.current.set(eventId, timeout);
  }

  return { refetchWithPolling, refetch: _refetch };
};
